import { useEffect, useState, useContext } from 'react';
import axios from '../../axios-interceptor';
import { environment } from '../../environments/environment';
import MultipleSelectChip from './SelectChip';
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { Paper, Button, Tooltip } from '@mui/material';
import { TextField } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import UpdateIcon from '@mui/icons-material/Update';
import PictureAsPdfTwoToneIcon from '@mui/icons-material/PictureAsPdfTwoTone';
import DlmDataGrid from './DlmDataGrid';
import { useTranslation } from 'react-i18next';
import { StateContext } from '../../contexts/StateContext';
import { ListItemTag } from '@brightlayer-ui/react-components/core/ListItemTag';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import UpdateFirmwareVersionComponent from './UpdateFirmwareVersionComponent';
import { useNavigate } from 'react-router-dom';
import semver from 'semver';


const baseURL = environment.baseURL;

const NewJobOverview = () => {
    const [sites, setSites] = useState([]);
    const [siteNames, setSiteNames] = useState([]);
    const [selectedSites, setSelectedSites] = useState([]);

    const [deviceTypes, setDeviceTypes] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState('');

    const [version, setVersion] = useState([]);
    const [selectedVersion, setSelectedVersion] = useState('');
    const [versionPrefixMap, setVersionPrefixMap] = useState({});

    const [filterString, setFilterString] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [rows, setRows] = useState([]);
    const [displayedRows, setDisplayedRows] = useState([]);
    const [availableFirmware, setAvailableFirmware] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [isExportEnabled, setIsExportEnabled] = useState(false);
    const [isUpdateAllEnabled, setIsUpdateAllEnabled] = useState(false);
    const [openFirmwareDialog, setOpenFirmwareDialog] = useState(false);
    const [firmwareUpdateProp, setFirmwareUpdateProp] = useState(null);
    const [deviceInfo, setDeviceInfo] = useState([]);
    const [isApplyFilterDisabled, setIsApplyFilterDisabled] = useState(true);

    const { t } = useTranslation();
    const { setPageHeader } = useContext(StateContext);
    const user = JSON.parse(window.localStorage.getItem('userDetails'));

    useEffect(() => {
        setPageHeader(t('translation:pageHeader.new_job'));
    }, [t, setPageHeader]);
    const navigate = useNavigate();

    const columns = [
        {
            field: 'deviceId',
            headerName: 'Device ID',
            width: 350,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Tooltip
                    title={params.value}
                    arrow
                    placement="top"
                    componentsProps={{
                        tooltip: {
                            sx: {
                                bgcolor: '#424E54',
                                fontSize: '12px',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                '& .MuiTooltip-arrow': {
                                    color: '#424E54',
                                },
                            },
                        },
                    }}
                >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {params.value}
                    </span>
                </Tooltip>
            ),
        },
        {
            field: 'serialNumber',
            headerName: 'Serial No.',
            width: 150,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Tooltip
                    title={params.value}
                    arrow
                    placement="top"
                    componentsProps={{
                        tooltip: {
                            sx: {
                                bgcolor: '#424E54',
                                fontSize: '12px',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                '& .MuiTooltip-arrow': {
                                    color: '#424E54',
                                },
                            },
                        },
                    }}
                >
                    <span>{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: 'site',
            headerName: 'Site',
            width: 150,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Tooltip
                    title={params.value}
                    arrow
                    placement="top"
                    componentsProps={{
                        tooltip: {
                            sx: {
                                bgcolor: '#424E54',
                                fontSize: '12px',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                '& .MuiTooltip-arrow': {
                                    color: '#424E54',
                                },
                            },
                        },
                    }}
                >
                    <span>{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: 'version',
            headerName: 'Firmware Version',
            width: 150,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Tooltip
                    title={`Version: ${params.value}`}
                    arrow
                    placement="top"
                    componentsProps={{
                        tooltip: {
                            sx: {
                                bgcolor: '#424E54',
                                fontSize: '12px',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                '& .MuiTooltip-arrow': {
                                    color: '#424E54',
                                },
                            },
                        },
                    }}
                >
                    <span>{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: 'lastUpdated',
            headerName: 'Last Firmware Updated',
            width: 250,
            headerAlign: 'center',
            align: 'center',
            // The cell shows a formatted date string, so sort on the raw timestamp instead.
            sortComparator: (v1, v2, param1, param2) =>
                (param1.api.getRow(param1.id)?.lastUpdatedTimestamp || 0) -
                (param2.api.getRow(param2.id)?.lastUpdatedTimestamp || 0),
            renderCell: (params) => (
                <Tooltip
                    title={`Last Updated: ${params.value}`}
                    arrow
                    placement="top"
                    componentsProps={{
                        tooltip: {
                            sx: {
                                bgcolor: '#424E54',
                                fontSize: '12px',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                '& .MuiTooltip-arrow': {
                                    color: '#424E54',
                                },
                            },
                        },
                    }}
                >
                    <span>{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: 'status',
            headerName: 'Update Status',
            width: 300,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Tooltip
                    title={
                        params.value === 'UPDATE AVAILABLE'
                            ? 'A firmware update is available for this device'
                            : 'This device is running the latest firmware version'
                    }
                    arrow
                    placement="top"
                    componentsProps={{
                        tooltip: {
                            sx: {
                                bgcolor: '#424E54',
                                fontSize: '12px',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                '& .MuiTooltip-arrow': {
                                    color: '#424E54',
                                },
                            },
                        },
                    }}
                >
                    <ListItemTag
                        label={params.value}
                        backgroundColor={params.value === 'UPDATE AVAILABLE' ? '#ffffff' : '#4caf50'}
                        fontColor={params.value === 'UPDATE AVAILABLE' ? '#000000' : '#ffffff'}
                        style={{
                            borderWidth: params.value === 'UPDATE AVAILABLE' ? '1px' : '0',
                            borderStyle: params.value === 'UPDATE AVAILABLE' ? 'solid' : 'none',
                            borderColor: params.value === 'UPDATE AVAILABLE' ? '#000000' : 'transparent',
                            fontWeight: 700,
                            fontSize: '10px',
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            fontFamily: 'Open Sans',
                        }}
                    />
                </Tooltip>
            ),
        },
        {
            field: 'actionUpdate',
            headerName: '',
            width: 100,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) =>
                params.value ? (
                    <Tooltip
                        title="Click to update firmware for this device"
                        arrow
                        placement="left"
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    bgcolor: '#424E54',
                                    fontSize: '12px',
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    '& .MuiTooltip-arrow': {
                                        color: '#424E54',
                                    },
                                },
                            },
                        }}
                    >
                        <Button
                            className="update-btn"
                            variant="outlined"
                            color="primary"
                            startIcon={<UpdateIcon />}
                            onClick={() => handleUpdateClick(params.row)}
                        >
                            Update
                        </Button>
                    </Tooltip>
                ) : null,
        },
    ];

    useEffect(() => {
        axios
            .get(`${baseURL}/assets/firmware/available`)
            .then((res) => {
                setAvailableFirmware(res.data);
            })
            .catch(() => setAvailableFirmware([]));
    }, []);

    const generateRows = (devicesData) => {
        const siteMap = new Map(sites.map((site) => [site.id, site.name]));
        const firmwareMap = new Map(availableFirmware.map((fw) => [fw.deviceType, fw.version]));

        return devicesData.map((device, index) => {
            const siteName = siteMap.get(device.siteId) || 'Unknown';
            const latestVersion = firmwareMap.get(device.deviceType) || device.version;
            const isUpdateAvailable = semver.gt(
                semver.coerce(latestVersion)?.version || '0.0.0',
                semver.coerce(device.version)?.version || '0.0.0'
            );

            return {
                id: device.deviceId,
                deviceId: device.deviceId,
                serialNumber: device.serialNumber,
                site: siteName,
                version: device.version,
                lastUpdated: device.dateTime
                    ? new Date(device.dateTime)
                          .toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true,
                          })
                          .replace(',', ', ')
                    : 'N/A',
                lastUpdatedTimestamp: device.dateTime ? new Date(device.dateTime).getTime() || 0 : 0,
                status: isUpdateAvailable ? 'UPDATE AVAILABLE' : 'UP TO DATE',
                actionUpdate: isUpdateAvailable,
            };
        })
        .sort((a, b) =>
            (a.serialNumber || '').localeCompare(b.serialNumber || '', undefined, {
                sensitivity: 'base',
                numeric: true,
            })
        );
    };

    useEffect(() => {
        const userId = localStorage.getItem('userId');
        axios
            .get(`${baseURL}/accesscontrol/sites?userId=${userId}`)
            .then((res) => {
                const siteList = Array.isArray(res.data) ? [...res.data] : [];
                siteList.sort((a, b) => (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' }));
                setSites(siteList);
            })
            .catch(() => setSites([]));
    }, []);

    useEffect(() => {
        displayedRows?.length > 0 ?  setIsUpdateAllEnabled(true) : setIsUpdateAllEnabled(false);
    },[rows,displayedRows])

    useEffect(() => {
        setSiteNames(sites?.map((site) => site.name));
    }, [sites]);

    useEffect(() => {
        const selectedSiteIds = sites.filter((site) => selectedSites.includes(site.name)).map((site) => site.id);
        const params = new URLSearchParams();

        const take = process.env.REACT_APP_UIGetDevicesMaxTake || 20;
        params.append('take', take);

        if (selectedSiteIds.length > 0) {
            params.append('siteIds', selectedSiteIds.join(','));
        }

        if (selectedDevice) {
            params.append('deviceType', selectedDevice);
        }

        if (selectedVersion) {
            params.append('version', versionPrefixMap[selectedVersion] || selectedVersion);
        }

        const filterStr = params.toString();
        setFilterString(filterStr);
    }, [selectedSites, selectedDevice, selectedVersion, sites, versionPrefixMap]);

    useEffect(() => {
        if (selectedSites.length === 0) {
            return;
        }
        const selectedSiteIds = sites.filter((site) => selectedSites.includes(site.name)).map((site) => site.id);
        if (selectedSiteIds.length === 0) {
            return;
        }

        const siteIdsQuery = selectedSiteIds.join(',');

        axios
            .get(`${baseURL}/assets/firmware/usage?siteIds=${siteIdsQuery}`)
            .then((res) => {
                const data = Array.isArray(res.data) ? res.data : [];
                const uniqueDeviceTypes = [
                    ...new Set(
                        data
                            .map((item) => item.deviceType)
                            .filter((deviceType) => typeof deviceType === 'string' && deviceType.trim())
                    ),
                ].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
                setDeviceTypes(uniqueDeviceTypes);
                if (selectedDevice) {
                    const filtered = data.filter((item) => item.deviceType === selectedDevice);
                    const prefixMap = {};
                    const uniqueVersions = [
                        ...new Set(
                            filtered
                                .map((item) => {
                                    const coerced = semver.coerce(item.version)?.version;
                                    if (coerced && /^v/i.test(item.version)) {
                                        prefixMap[coerced] = item.version;
                                    }
                                    return coerced;
                                })
                                .filter(Boolean)
                        ),
                    ];
                    setVersion(uniqueVersions);
                    setVersionPrefixMap(prefixMap);
                } else {
                    setVersion([]);
                    setVersionPrefixMap({});
                }
            })
            .catch(() => {
                setDeviceTypes([]);
                setVersion([]);
            });
    }, [selectedSites, sites, selectedDevice]);

    useEffect(() => {
        setIsApplyFilterDisabled(!(selectedSites?.length && selectedDevice));
     },[selectedSites,selectedDevice])

    const handleDeviceTypeChange = (event) => {
        setSelectedDevice(event.target.value);
    };

    const handleVersionChange = (event) => {
        setSelectedVersion(event.target.value);
    };

    const detectSelectedRows = (newSelection) => {
        setSelectedRows(newSelection);
        const selectedRowsData = displayedRows.filter((row) => newSelection.includes(row.id));

        if (selectedRowsData.length > 0) {
            setIsExportEnabled(true);
        } else {
            setIsExportEnabled(false);
        }

    };

    const handleStartUpdate = (deviceData,selectedVersion) => {
        const firmwareImageId = availableFirmware.find(el => el.deviceType === deviceData?.deviceType && (el.version === selectedVersion))?.id;
        const payload = {
            prioritizedUpdateOverDREvent: true,
            firmwareImageId: firmwareImageId,
            deviceType: deviceData?.deviceType,
            targetFirmwareVersion: selectedVersion,
            siteDevices: [
                {
                    siteId: deviceData?.siteId,
                    deviceIds: [deviceData?.deviceId],
                },
            ],
        };
        axios
            .post(`${baseURL}/dlm`, payload)
            .then((res) => {
                navigate('/dlm');
            })
            .catch(() => {});
    };

    const handleStartUpdateAll = (devicesData,selectedVersion) => {
        // Group devices by deviceType for batch processing
        const devicesByType = devicesData.reduce((acc, device) => {
            if (!acc[device.deviceType]) {
                acc[device.deviceType] = [];
            }
            acc[device.deviceType].push(device);
            return acc;
        }, {});
        
        // Create payloads for each device type
        const updatePromises = Object.entries(devicesByType).map(([deviceType, devices]) => {
            const matchedFirmwares = availableFirmware.filter((el) => {
                const coercedElVersion = semver.coerce(el.version);
                const coercedSelectedVersion = semver.coerce(selectedVersion);
                return el.deviceType === deviceType && coercedElVersion?.version === coercedSelectedVersion?.version;
            });
           
            const firmwareImageId = matchedFirmwares[0]?.id;

            const devicesBySite = devices.reduce((acc, device) => {
                if (!acc[device.siteId]) {
                    acc[device.siteId] = [];
                }
                acc[device.siteId].push(device.deviceId);
                return acc;
            }, {});

            const siteDevices = Object.entries(devicesBySite).map(([siteId, deviceIds]) => ({
                siteId,
                deviceIds,
            }));

            const payload = {
                prioritizedUpdateOverDREvent: true,
                firmwareImageId: firmwareImageId,
                deviceType: deviceType,
                targetFirmwareVersion: selectedVersion,
                siteDevices: siteDevices,
            };

            return axios.post(`${baseURL}/dlm`, payload);
        });

        Promise.all(updatePromises)
            .then(() => {
                navigate('/dlm');
            })
            .catch(() => {});
    };

    const handleUpdateClick = (rowData) => {
        const deviceData = deviceInfo?.find((row) => row.deviceId === rowData.deviceId);

        setFirmwareUpdateProp({
            open: true,
            onClose: () => setOpenFirmwareDialog(false),
            onConfirm: (selectedVersion) => handleStartUpdate(deviceData, selectedVersion),
            deviceCount: 1,
            deviceType: deviceData?.deviceType,
            currentVersions: [deviceData?.version],
            userEmail: user?.email,
            estimatedTime: '90 minutes', // keeping this value static for now
            latestVersion: availableFirmware
                .filter((fw) => fw.deviceType === deviceData?.deviceType)
                .map((fw) => fw.version),
        });
        setOpenFirmwareDialog(true);
    };

    const handleUpdateAllClick = (rowsData) => {
        const devicesData = rowsData
            .map((row) => deviceInfo?.find((device) => device.deviceId === row.deviceId))
            .filter(Boolean);

        // Collect all unique device types and versions
        const deviceTypes = [...new Set(devicesData.map((d) => d.deviceType))];
        const currentVersions = [...new Set(devicesData.map((d) => d.version))];

        // Get latest versions for all device types
        const latestVersions = [
            ...new Set(
                devicesData.flatMap((d) =>
                    availableFirmware.filter((fw) => fw.deviceType === d.deviceType).map((fw) => fw.version)
                )
            ),
        ];

        setFirmwareUpdateProp({
            open: true,
            onClose: () => setOpenFirmwareDialog(false),
            onConfirm: (selectedVersion) => handleStartUpdateAll(devicesData, selectedVersion),
            deviceCount: devicesData.length,
            deviceType: deviceTypes.join(', '),
            currentVersions: currentVersions,
            userEmail: user?.email,
            estimatedTime: '90 minutes', // keeping this value static for now
            latestVersion: latestVersions,
        });
        setOpenFirmwareDialog(true);
    };

    const handleExportClicked = () => {
        const selectedRowsData = displayedRows.filter((row) => selectedRows.includes(row.id));
        if (selectedRowsData.length === 0) {
            return;
        }

        const doc = new jsPDF();
        doc.setFontSize(16);
        doc.text('Device Firmware Report', 14, 15);
        doc.setFontSize(10);
        doc.text(
            `Generated: ${new Date().toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
            })}`,
            14,
            22
        );

        const tableColumn = ['Device ID', 'Serial No.', 'Site', 'Version', 'Last Updated', 'Status'];
        const tableRows = selectedRowsData.map((row) => [
            row.deviceId,
            row.serialNumber,
            row.site,
            row.version,
            row.lastUpdated,
            row.status,
        ]);

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 28,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [66, 78, 84] },
        });

        doc.save('Device_Firmware_Report.pdf');
    };

    useEffect(() => {
        if (!searchTerm.trim()) {
            setDisplayedRows(rows);
            return;
        }

        const lowerSearchTerm = searchTerm.toLowerCase();
        const filtered = rows.filter((row) =>
            Object.entries(row).some(([key, value]) => {
                if (key === 'lastUpdatedTimestamp' || value === null || value === undefined) return false;
                return String(value).toLowerCase().includes(lowerSearchTerm);
            })
        );
        setDisplayedRows(filtered);
    }, [searchTerm, rows]);

    const applyFilterHandler = () => {
        axios
            .get(`${baseURL}/assets/firmware/devices?${filterString}`)
            .then((res) => {
                const generatedRows = generateRows(res.data);
                setDeviceInfo(res.data);
                setRows(generatedRows);
            })
            .catch((err) => {
                setRows([]);
                setDeviceInfo([]);
            });
    };

    return (
        <div className="new-job-overview" data-testid="new-job-overview-page">
            <div className="filters-wrapper" data-testid="filters-section">
                <MultipleSelectChip
                    label={t('translation:pages.dlm.new_job.chip_label')}
                    options={siteNames}
                    value={selectedSites}
                    onChange={setSelectedSites}
                />
                <Box sx={{ minWidth: 80 }} className="device-type-select-box">
                    <FormControl fullWidth variant="outlined">
                        <InputLabel id="input-device-types" variant="outlined">
                            {t('translation:pages.dlm.new_job.device_type_label')}
                        </InputLabel>
                        <Select
                            labelId="input-device-types"
                            value={selectedDevice}
                            label={t('translation:pages.dlm.new_job.device_type_label')}
                            onChange={handleDeviceTypeChange}
                        >
                            {deviceTypes?.map((ver, index) => (
                                <MenuItem key={index} value={ver}>
                                    {ver}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{ minWidth: 80 }} className="version-select-box">
                    <FormControl fullWidth variant="outlined">
                        <InputLabel id="input-version" variant="outlined">
                            {t('translation:pages.dlm.new_job.version_label')}
                        </InputLabel>
                        <Select
                            labelId="input-version"
                            value={selectedVersion}
                            label={t('translation:pages.dlm.new_job.version_label')}
                            onChange={handleVersionChange}
                        >
                            {version?.map((ver, index) => (
                                <MenuItem key={index} value={ver}>
                                    {ver}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Button
                    className="apply-filters-button"
                    variant="contained"
                    color="primary"
                    disabled={isApplyFilterDisabled}
                    onClick={applyFilterHandler}
                >
                    {t('translation:pages.dlm.new_job.apply_filter_btn')}
                </Button>
            </div>
            <div className="search-export-wrapper">
                <TextField
                    className="search-input"
                    variant="outlined"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('translation:pages.dlm.new_job.search_label')}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                <Button
                    className="export-list-btn"
                    variant="outlined"
                    color="primary"
                    disabled={!isExportEnabled}
                    startIcon={<PictureAsPdfTwoToneIcon />}
                    onClick={handleExportClicked}
                >
                    {t('translation:pages.dlm.new_job.export_btn')}
                </Button>
                <Button
                    className="update-all-btn"
                    variant="contained"
                    color="primary"
                    disabled={!isUpdateAllEnabled}
                    startIcon={<UpdateIcon />}
                    onClick={() => {
                        const selectedRowsData = displayedRows.filter((row) => selectedRows.includes(row.id));
                        selectedRowsData?.length > 0 ? handleUpdateAllClick(selectedRowsData) : handleUpdateAllClick(displayedRows);
                    }}
                >
                    {selectedRows.length > 0 && selectedRows.length !== displayedRows.length ? t('translation:pages.dlm.new_job.selection_all_btn') + ` (${selectedRows.length})` : t('translation:pages.dlm.new_job.update_all_btn')}
                </Button>
            </div>

            <div className="data-table">
                <Paper elevation={3} className="paper-data-grid">
                    <DlmDataGrid rows={displayedRows} columns={columns} onRowSelectionChange={detectSelectedRows} searchTerm={searchTerm?.length}   />
                </Paper>
            </div>
            {openFirmwareDialog && <UpdateFirmwareVersionComponent {...firmwareUpdateProp} />}
        </div>
    );
};

export default NewJobOverview;
